import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IntegrationsTab from './IntegrationsTab';
import ApplicationsTab from './ApplicationsTab';
import ContractTermsTab from './ContractTermsTab';
import { useAuth } from '../../contexts/AuthContext';
import UserTab from './UserTab';
import ExtensionsTab from './ExtensionsTab';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const TABS = [
  { id: 'integrations', label: 'Integrations', component: IntegrationsTab },
  { id: 'applications', label: 'Applications', component: ApplicationsTab },
  { id: 'contract-terms', label: 'Contract Terms', component: ContractTermsTab },
  { id: 'extensions', label: 'Extensions', component: ExtensionsTab },
  { id: 'user', label: 'User', component: UserTab },
];

const TAB_IDS = TABS.map((t) => t.id);

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const activeId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('tab');
    return TAB_IDS.includes(requested) ? requested : TABS[0].id;
  }, [location.search]);

  const ActiveComponent = TABS.find((t) => t.id === activeId)?.component || TABS[0].component;

  const handleTabClick = (id) => {
    if (id === activeId) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', id);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: false });
  };

  useEffect(() => {
      const token = getToken();
  
      if (!token) {
        logout();
        navigate('/login');
        return;
      }
  
      setIsLoading(true);
  }, []);
  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-gray-600 text-sm md:text-base">
          Configure integrations, branding, and user access. API base in use:{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{apiUrl}</code>
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Settings sections"
        className="flex flex-wrap gap-1 border-b border-gray-200 mb-6"
      >
        {TABS.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`settings-panel-${tab.id}`}
              id={`settings-tab-${tab.id}`}
              onClick={() => handleTabClick(tab.id)}
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <section
        role="tabpanel"
        id={`settings-panel-${activeId}`}
        aria-labelledby={`settings-tab-${activeId}`}
      >
        <ActiveComponent />
      </section>
    </div>
  );
};

export default SettingsPage;
